import { useIsSupportOperator } from '@/hooks/useIsSupportOperator';
import { Card } from '@/components/ui/card';
import { Loader2, ShieldAlert } from 'lucide-react';

/**
 * CORE-BJ — route gate for support surfaces.
 *
 * Support surfaces were previously behind a generic
 * `ProtectedRoute requiredRoles={['MANAGER','OWNER']}`, which is a shop role
 * and therefore not the constitutional designation. They now require the
 * explicit support-operator grant.
 *
 * This is presentation only. Every support surface's data is independently
 * gated by RLS asking `is_support_operator`, so a bypassed route renders
 * nothing rather than leaking privileged data.
 */
export const SupportOperatorRoute = ({ children }: { children: React.ReactNode }) => {
  const { data: isSupportOperator, isLoading } = useIsSupportOperator();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isSupportOperator) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] p-6">
        <Card className="max-w-md p-6 space-y-2">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            <h2 className="font-semibold">Support access required</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Entering a customer's shop requires an OptiBay support-operator designation.
            Your account does not currently hold one.
          </p>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};
